import React, { useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { OccurrenceCount } from '../types';

interface ReviewTimeLineProps {
    data: OccurrenceCount[]; // Assuming same data structure as before
}

const ReviewTimeLine: React.FC<ReviewTimeLineProps> = ({ data }) => {
    // Function to parse date from "DD/MM/YYYY" format
    const parseDate = (dateStr: string): Date => {
        const [day, month, year] = dateStr.split('/').map(Number);
        return new Date(year, month - 1, day); // Month is 0-indexed in JS Date
    };

    // Generate the last 60 days as date strings in the format "DD/MM/YYYY"
    const generateLast60Days = (): { date: Date; formattedDate: string }[] => {
        const days = [];
        const today = new Date();

        for (let i = 59; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(today.getDate() - i);

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            const formattedDate = `${day}/${month}/${year}`;
            days.push({ date, formattedDate });
        }

        return days;
    };

    // Process the data for the timeline
    const timelineData = useMemo(() => {
        const last60Days = generateLast60Days();

        // Create a lookup map for quick access to data points
        const dataMap = new Map();
        data.forEach((item) => {
            try {
                // Assuming item.name is the date string in "DD/MM/YYYY" format
                dataMap.set(item.name, item.occurrence);
            } catch (error) {
                console.error(
                    `Error processing data item: ${item.name}`,
                    error,
                );
            }
        });

        // Generate the complete timeline with all 60 days
        const timelinePoints = last60Days.map(({ formattedDate }) => {
            return {
                x: formattedDate,
                y: dataMap.has(formattedDate) ? dataMap.get(formattedDate) : 0,
            };
        });

        return [
            {
                id: 'Reviews',
                data: timelinePoints,
            },
        ];
    }, [data]);

    return (
        <div className="chart">
            <ResponsiveLine
                data={timelineData}
                margin={{ top: 20, right: 20, bottom: 60, left: 50 }}
                xScale={{ type: 'point' }}
                yScale={{
                    type: 'linear',
                    min: 0,
                    max: 'auto',
                }}
                curve="monotoneX"
                axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -45,
                    legendOffset: 50,
                    legendPosition: 'middle',
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legendOffset: -40,
                    legendPosition: 'middle',
                }}
                colors={['#e5b611']} // Match the color from the bar chart
                pointSize={8}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                enableSlices="x"
                enableArea={true}
                areaOpacity={0.1}
                crosshairType="x"
                tooltip={({ point }) => (
                    <div
                        style={{
                            padding: 12,
                            color: '#fff',
                            background: '#333',
                            borderRadius: 3,
                        }}
                    >
                        <strong>Date: {point.data.x.toString()}</strong>
                        <br />
                        <span>
                            Reviews: <strong>{point.data.y.toString()}</strong>
                        </span>
                    </div>
                )}
                theme={{
                    grid: {
                        line: {
                            stroke: '#dddddd',
                            strokeWidth: 1,
                        },
                    },
                    tooltip: {
                        container: {
                            background: '#333',
                            color: '#fff',
                            fontSize: 12,
                            borderRadius: 2,
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
                            padding: '5px 9px',
                        },
                    },
                }}
            />
        </div>
    );
};

export default ReviewTimeLine;
